/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const ScientificEditorSystem = ({ 
  initialValue = '', 
  onSave, 
  placeholder = 'Enter experimental parameters' 
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [primaryText, setPrimaryText] = useState(initialValue);
  const [secondaryText, setSecondaryText] = useState(initialValue);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lineNumbers, setLineNumbers] = useState(['1']);
  const [analysisResults, setAnalysisResults] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [systemStatus, setSystemStatus] = useState('READY');
  const [executionHistory, setExecutionHistory] = useState([]);
  const [cpuUsage, setCpuUsage] = useState(0);
  const [memoryUsage, setMemoryUsage] = useState(0);
  const [networkLatency, setNetworkLatency] = useState(0);
  const [debouncedText, setDebouncedText] = useState(initialValue);
  
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const performanceMonitorRef = useRef(null);
  const textAnalysisDebounceRef = useRef(null);
  const lineNumberDebounceRef = useRef(null);

  // Auto-focus on mode change
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isDarkMode]);

  // Debounce text analysis - only run expensive calculations after user stops typing
  useEffect(() => {
    const currentText = isDarkMode ? secondaryText : primaryText;
    
    if (textAnalysisDebounceRef.current) {
      clearTimeout(textAnalysisDebounceRef.current);
    }
    
    textAnalysisDebounceRef.current = setTimeout(() => {
      setDebouncedText(currentText);
    }, 300); // Wait 300ms after user stops typing
    
    return () => {
      if (textAnalysisDebounceRef.current) {
        clearTimeout(textAnalysisDebounceRef.current);
      }
    };
  }, [primaryText, secondaryText, isDarkMode]);

  // Calculate text entropy (only on debounced text)
  const calculateEntropy = useCallback((text) => {
    if (!text || text.length === 0) return 0;
    const freq = {};
    for (let char of text) {
      freq[char] = (freq[char] || 0) + 1;
    }
    const len = text.length;
    return Object.values(freq).reduce((entropy, count) => {
      const p = count / len;
      return entropy - p * Math.log2(p);
    }, 0);
  }, []);

  // Calculate readability score (only on debounced text)
  const calculateReadability = useCallback((text) => {
    if (!text || text.length === 0) return 0;
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
    const words = text.split(/\s+/).filter(w => w.length > 0).length;
    const syllables = text.match(/[aeiouAEIOU]/g)?.length || 0;
    
    if (sentences === 0 || words === 0) return 0;
    
    return 206.835 - (1.015 * (words / sentences)) - (84.6 * (syllables / words));
  }, []);

  // Reduce performance monitoring frequency
  useEffect(() => {
    const interval = setInterval(() => {
      setCpuUsage(Math.random() * 100);
      setMemoryUsage(Math.random() * 100);
      setNetworkLatency(Math.random() * 500);
    }, 2000); // Reduced from 1000ms to 2000ms
    return () => clearInterval(interval);
  }, []);

  // Fast text analysis for immediate feedback (lightweight calculations only)
  const fastAnalysis = useMemo(() => {
    const text = isDarkMode ? secondaryText : primaryText;
    const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
    const charCount = text.length;
    const lineCount = text.split('\n').length;
    const avgWordsPerLine = wordCount / Math.max(lineCount, 1);
    
    return {
      wordCount,
      charCount,
      lineCount,
      avgWordsPerLine: avgWordsPerLine.toFixed(2),
      complexity: text.match(/[A-Z]/g)?.length || 0
    };
  }, [primaryText, secondaryText, isDarkMode]);

  // Heavy text analysis for detailed metrics (debounced)
  const detailedAnalysis = useMemo(() => {
    if (!debouncedText) return { entropy: '0.000', readabilityScore: '0.00' };
    
    const entropy = calculateEntropy(debouncedText);
    const readabilityScore = calculateReadability(debouncedText);
    
    return {
      entropy: entropy.toFixed(3),
      readabilityScore: readabilityScore.toFixed(2)
    };
  }, [debouncedText, calculateEntropy, calculateReadability]);

  // Combined analysis for display
  const textAnalysis = useMemo(() => ({
    ...fastAnalysis,
    ...detailedAnalysis
  }), [fastAnalysis, detailedAnalysis]);

  // Update line numbers for dark mode (heavily debounced and optimized) - DISABLED FOR PERFORMANCE
  /*
  useEffect(() => {
    if (isDarkMode) {
      if (lineNumberDebounceRef.current) {
        clearTimeout(lineNumberDebounceRef.current);
      }
      
      lineNumberDebounceRef.current = setTimeout(() => {
        const lines = secondaryText.split('\n');
        const maxLines = Math.min(lines.length, 1000); // Limit for performance
        const numbers = Array.from({ length: maxLines }, (_, index) => 
          (index + 1).toString().padStart(3, '0')
        );
        setLineNumbers(numbers);
      }, 500); // Increased debounce for line numbers
    }
    
    return () => {
      if (lineNumberDebounceRef.current) {
        clearTimeout(lineNumberDebounceRef.current);
      }
    };
  }, [secondaryText, isDarkMode]);
  */

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    if (lineNumbersRef.current && textareaRef.current) {
      requestAnimationFrame(() => {
        if (lineNumbersRef.current && textareaRef.current) {
          lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
        }
      });
    }
  }, []);

  // Optimized text change handlers
  const handlePrimaryTextChange = useCallback((e) => {
    setPrimaryText(e.target.value);
    if (error) setError('');
  }, [error]);

  const handleSecondaryTextChange = useCallback((e) => {
    setSecondaryText(e.target.value);
    if (error) setError('');
  }, [error]);

  // Memoized line numbers component
  const LineNumberPanel = React.memo(({ numbers }) => (
    <div 
      className="line-number-panel" 
      ref={lineNumbersRef}
    >
      <div className="line-header">LN</div>
      {numbers.map((number, index) => (
        <div key={`line-${index}`} className="line-number-item">
          {number}
        </div>
      ))}
    </div>
  ));

  // Memoized dark mode textarea to prevent unnecessary re-renders
  const DarkModeTextarea = React.memo(({ value, onChange, onScroll, placeholder, disabled }) => (
    <textarea
      ref={textareaRef}
      className="advanced-textarea"
      value={value}
      onChange={onChange}
      onScroll={onScroll}
      placeholder={placeholder}
      disabled={disabled}
      spellCheck="false"
    />
  ));

  // Advanced submit handler with performance tracking
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    const startTime = performance.now();
    const text = isDarkMode ? secondaryText : primaryText;
    const trimmedText = text.trim();
    
    if (!trimmedText) {
      setError(`ERROR: ${isDarkMode ? 'SECONDARY_INPUT_EMPTY' : 'PRIMARY_INPUT_EMPTY'}`);
      return;
    }
    
    setIsProcessing(true);
    setSystemStatus('PROCESSING');
    
    Promise.resolve()
      .then(() => {
        // Simulate complex processing
        return new Promise(resolve => setTimeout(resolve, Math.random() * 2000));
      })
      .then(() => onSave(trimmedText))
      .then(() => {
        const endTime = performance.now();
        const executionTime = endTime - startTime;
        
        setExecutionHistory(prev => [{
          timestamp: new Date().toISOString(),
          operation: isDarkMode ? 'SECONDARY_SAVE' : 'PRIMARY_SAVE',
          executionTime: executionTime.toFixed(2),
          textLength: trimmedText.length,
          status: 'SUCCESS'
        }, ...prev.slice(0, 9)]);
        
        if (isDarkMode) {
          setSecondaryText('');
        } else {
          setPrimaryText('');
        }
        setError('');
        setSystemStatus('SUCCESS');
      })
      .catch(err => {
        console.error("System Error:", err);
        setError(`CRITICAL_ERROR: ${isDarkMode ? 'SECONDARY_SAVE_FAILED' : 'PRIMARY_SAVE_FAILED'}`);
        setSystemStatus('ERROR');
      })
      .finally(() => {
        setIsProcessing(false);
        setTimeout(() => setSystemStatus('READY'), 2000);
      });
  }, [isDarkMode, secondaryText, primaryText, onSave]);

  // Advanced undo with history tracking
  const handleUndo = useCallback(() => {
    if (isDarkMode && secondaryText.length > 0) {
      setSecondaryText(prev => prev.slice(0, -1));
      setExecutionHistory(prev => [{
        timestamp: new Date().toISOString(),
        operation: 'UNDO_OPERATION',
        executionTime: '0.01',
        textLength: secondaryText.length - 1,
        status: 'SUCCESS'
      }, ...prev.slice(0, 9)]);
    }
  }, [isDarkMode, secondaryText]);

  // Toggle with system logging
  const toggleMode = useCallback(() => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      setExecutionHistory(prev => [{
        timestamp: new Date().toISOString(),
        operation: `MODE_SWITCH_${newMode ? 'DARK' : 'LIGHT'}`,
        executionTime: '0.05',
        textLength: 0,
        status: 'SUCCESS'
      }, ...prev.slice(0, 9)]);
      return newMode;
    });
    setError('');
  }, []);

  // Primary Scientific Editor Component
  const PrimaryEditor = () => (
    <div className="primary-editor">
      <div className="system-header">
        <div className="system-info">
          <div className="system-title">SCIENTIFIC ANALYSIS TERMINAL</div>
          <div className="system-version">v2.4.7-beta</div>
        </div>
        <div className="control-panel">
          <button
            onClick={toggleMode}
            className="control-button dark-mode-btn"
            title="Switch to Dark Analysis Mode"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
            DARK_MODE
          </button>
          <div className="system-status" data-status={systemStatus}>
            {systemStatus}
          </div>
        </div>
      </div>

      <div className="metrics-panel">
        <div className="metric-group">
          <div className="metric">
            <span className="metric-label">CPU:</span>
            <span className="metric-value">{cpuUsage.toFixed(1)}%</span>
            <div className="metric-bar">
              <div className="metric-fill" style={{width: `${cpuUsage}%`}}></div>
            </div>
          </div>
          <div className="metric">
            <span className="metric-label">MEM:</span>
            <span className="metric-value">{memoryUsage.toFixed(1)}%</span>
            <div className="metric-bar">
              <div className="metric-fill" style={{width: `${memoryUsage}%`}}></div>
            </div>
          </div>
          <div className="metric">
            <span className="metric-label">NET:</span>
            <span className="metric-value">{networkLatency.toFixed(0)}ms</span>
          </div>
        </div>
        
        <div className="analysis-panel">
          <div className="analysis-title">TEXT ANALYSIS</div>
          <div className="analysis-grid">
            <div>WORDS: {textAnalysis.wordCount}</div>
            <div>CHARS: {textAnalysis.charCount}</div>
            <div>LINES: {textAnalysis.lineCount}</div>
            <div>AVG_WPL: {textAnalysis.avgWordsPerLine}</div>
            <div>ENTROPY: {textAnalysis.entropy}</div>
            <div>READABILITY: {textAnalysis.readabilityScore}</div>
          </div>
        </div>
      </div>

      <div className="input-section">
        <div className="input-header">
          <div className="input-label">PRIMARY INPUT BUFFER</div>
          <div className="input-counter">{primaryText.length}/2048</div>
        </div>
        
        <div className="input-container">
          <input
            type="text"
            className={`scientific-input ${error ? 'error-state' : ''}`}
            value={primaryText}
            onChange={handlePrimaryTextChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(e);
              }
            }}
            placeholder={placeholder}
            autoFocus
            maxLength={2048}
            disabled={isProcessing}
          />
          {error && <div className="error-display">{error}</div>}
        </div>
      </div>

      <div className="execution-panel">
        <button 
          onClick={handleSubmit}
          className="execute-button"
          disabled={!primaryText.trim() || isProcessing}
        >
          {isProcessing ? (
            <>
              <div className="processing-spinner"></div>
              <span>EXECUTING...</span>
            </>
          ) : (
            <>
              <div className="execute-icon"></div>
              <span>EXECUTE</span>
            </>
          )}
        </button>
      </div>

      {executionHistory.length > 0 && (
        <div className="history-panel">
          <div className="history-title">EXECUTION HISTORY</div>
          <div className="history-list">
            {executionHistory.map((entry, index) => (
              <div key={index} className="history-entry">
                <span className="history-time">{entry.timestamp.split('T')[1].split('.')[0]}</span>
                <span className="history-op">{entry.operation}</span>
                <span className="history-duration">{entry.executionTime}ms</span>
                <span className={`history-status ${entry.status.toLowerCase()}`}>{entry.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Dark Advanced Editor Component (Simplified for Performance)
  const DarkAdvancedEditor = () => (
    <div className="dark-editor">
      <div className="editor-frame">
        {/* Advanced Control Strip */}
        <div className="control-strip">
          <div className="strip-left">
            <button
              type="button"
              onClick={toggleMode}
              className="strip-button mode-toggle"
              title="Switch to Primary Editor"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
              LIGHT_MODE
            </button>

            <button
              type="button"
              onClick={handleUndo}
              className="strip-button undo-btn"
              disabled={isProcessing || secondaryText.length === 0}
              title="Undo Operation"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7v6h6"/>
                <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/>
              </svg>
              UNDO
            </button>
          </div>

          <div className="strip-center">
            <div className="system-metrics">
              <span>CPU: {cpuUsage.toFixed(1)}%</span>
              <span>|</span>
              <span>MEM: {memoryUsage.toFixed(1)}%</span>
              <span>|</span>
              <span>LAT: {networkLatency.toFixed(0)}ms</span>
            </div>
          </div>

          <div className="strip-right">
            <div className="word-counter">
              TOKENS: {textAnalysis.wordCount} | ENTROPY: {textAnalysis.entropy}
            </div>
            
            <button
              type="button"
              onClick={handleSubmit}
              className="strip-button save-btn"
              disabled={!secondaryText.trim() || isProcessing}
            >
              {isProcessing ? (
                <div className="mini-spinner"></div>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                  <polyline points="17,21 17,13 7,13 7,21"/>
                  <polyline points="7,3 7,8 15,8"/>
                </svg>
              )}
              SAVE
            </button>
          </div>
        </div>

        {/* Simplified Editor Interface */}
        <div className="simplified-editor-interface">
          {/* Simple Text Area - No Line Numbers */}
          <textarea
            ref={textareaRef}
            className="dark-simple-textarea"
            value={secondaryText}
            onChange={handleSecondaryTextChange}
            placeholder="Initialize secondary analysis buffer..."
            disabled={isProcessing}
            spellCheck="false"
            autoFocus={isDarkMode}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            tabIndex={0}
          />

          {/* Compact Status Panel */}
          <div className="compact-status-panel">
            <div className="status-row">
              <span>MODE: {isDarkMode ? 'DARK_ANALYSIS' : 'PRIMARY'}</span>
              <span>STATE: <span className={`status-value ${systemStatus.toLowerCase()}`}>{systemStatus}</span></span>
            </div>
            <div className="status-row">
              <span>BUFFER: {secondaryText.length}/∞</span>
              <span>LINES: {textAnalysis.lineCount}</span>
            </div>
            <div className="status-row">
              <span>WORDS: {textAnalysis.wordCount}</span>
              <span>CHARS: {textAnalysis.charCount}</span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-console">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <span className="error-code">[ERR_001]</span>
            <span className="error-text">{error}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="scientific-system">
      {isDarkMode ? <DarkAdvancedEditor /> : <PrimaryEditor />}
      
      <style jsx>{`
        .scientific-system {
          width: 100%;
          height: 100vh;
          font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
          background: #000000;
          color: #00ff80;
          overflow: hidden;
        }

        /* Primary Editor Styles */
        .primary-editor {
          height: 100vh;
          background: linear-gradient(135deg, #001a0d 0%, #002612 100%);
          border: 1px solid #00ff80;
          display: flex;
          flex-direction: column;
          padding: 12px;
        }

        .system-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: rgba(0, 255, 128, 0.1);
          border: 1px solid #00ff80;
          border-radius: 4px;
          margin-bottom: 12px;
        }

        .system-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .system-title {
          font-size: 0.9rem;
          font-weight: bold;
          color: #00ff80;
          letter-spacing: 1px;
        }

        .system-version {
          font-size: 0.7rem;
          color: #66ff99;
          opacity: 0.8;
        }

        .control-panel {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .control-button {
          background: rgba(0, 255, 128, 0.15);
          border: 1px solid #00ff80;
          border-radius: 3px;
          color: #00ff80;
          padding: 6px 10px;
          font-size: 0.75rem;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 4px;
          letter-spacing: 0.5px;
        }

        .control-button:hover {
          background: rgba(0, 255, 128, 0.25);
          box-shadow: 0 0 8px rgba(0, 255, 128, 0.3);
        }

        .system-status {
          font-size: 0.75rem;
          font-weight: bold;
          padding: 4px 8px;
          border-radius: 3px;
          letter-spacing: 0.5px;
        }

        .system-status[data-status="READY"] {
          background: rgba(0, 255, 128, 0.2);
          color: #00ff80;
        }

        .system-status[data-status="PROCESSING"] {
          background: rgba(255, 165, 0, 0.2);
          color: #ffa500;
          animation: pulse 1s infinite;
        }

        .system-status[data-status="SUCCESS"] {
          background: rgba(0, 255, 0, 0.2);
          color: #00ff00;
        }

        .system-status[data-status="ERROR"] {
          background: rgba(255, 0, 0, 0.2);
          color: #ff0000;
        }

        .metrics-panel {
          display: flex;
          gap: 20px;
          margin-bottom: 16px;
          padding: 12px;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(0, 255, 128, 0.3);
          border-radius: 4px;
        }

        .metric-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
        }

        .metric {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
        }

        .metric-label {
          color: #66ff99;
          font-weight: bold;
          min-width: 35px;
        }

        .metric-value {
          color: #00ff80;
          min-width: 45px;
        }

        .metric-bar {
          flex: 1;
          height: 4px;
          background: rgba(0, 255, 128, 0.2);
          border-radius: 2px;
          overflow: hidden;
        }

        .metric-fill {
          height: 100%;
          background: #00ff80;
          transition: width 0.3s ease;
        }

        .analysis-panel {
          flex: 1;
          border-left: 1px solid rgba(0, 255, 128, 0.3);
          padding-left: 20px;
        }

        .analysis-title {
          font-size: 0.8rem;
          font-weight: bold;
          color: #66ff99;
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }

        .analysis-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;
          font-size: 0.75rem;
        }

        .analysis-grid > div {
          color: #00ff80;
          padding: 2px 4px;
          background: rgba(0, 255, 128, 0.1);
          border-radius: 2px;
        }

        .input-section {
          flex: 1;
          margin-bottom: 16px;
          display: flex;
          flex-direction: column;
        }

        .input-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 0.8rem;
        }

        .input-label {
          color: #66ff99;
          font-weight: bold;
          letter-spacing: 0.5px;
        }

        .input-counter {
          color: #00ff80;
          opacity: 0.8;
        }

        .input-container {
          position: relative;
          flex: 1;
        }

        .scientific-input {
          width: 100%;
          height: 120px;
          background: rgba(0, 0, 0, 0.5);
          border: 2px solid #00ff80;
          border-radius: 4px;
          color: #00ff80;
          font-family: inherit;
          font-size: 1rem;
          padding: 12px;
          outline: none;
          transition: all 0.2s ease;
        }

        .scientific-input:focus {
          border-color: #66ff99;
          box-shadow: 0 0 12px rgba(0, 255, 128, 0.3);
        }

        .scientific-input.error-state {
          border-color: #ff0000;
          box-shadow: 0 0 12px rgba(255, 0, 0, 0.3);
        }

        .scientific-input::placeholder {
          color: rgba(0, 255, 128, 0.5);
          font-style: italic;
        }

        .error-display {
          position: absolute;
          bottom: -24px;
          left: 0;
          font-size: 0.8rem;
          color: #ff4444;
          font-weight: bold;
          letter-spacing: 0.5px;
        }

        .execution-panel {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .execute-button {
          background: linear-gradient(135deg, rgba(0, 100, 50, 0.8) 0%, rgba(0, 150, 75, 0.8) 100%);
          border: 2px solid #00ff80;
          border-radius: 4px;
          color: #ffffff;
          padding: 12px 32px;
          font-family: inherit;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          letter-spacing: 1px;
          min-width: 180px;
          justify-content: center;
        }

        .execute-button:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(0, 120, 60, 0.9) 0%, rgba(0, 180, 90, 0.9) 100%);
          box-shadow: 0 0 16px rgba(0, 255, 128, 0.4);
          transform: translateY(-1px);
        }

        .execute-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .execute-icon {
          width: 16px;
          height: 16px;
          border: 2px solid currentColor;
          border-left: none;
          border-radius: 0 2px 2px 0;
          position: relative;
        }

        .execute-icon::before {
          content: '';
          position: absolute;
          left: -8px;
          top: 50%;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-left: 8px solid currentColor;
        }

        .processing-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #00ff80;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .history-panel {
          max-height: 200px;
          overflow-y: auto;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(0, 255, 128, 0.3);
          border-radius: 4px;
          padding: 8px;
        }

        .history-title {
          font-size: 0.8rem;
          font-weight: bold;
          color: #66ff99;
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }

        .history-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .history-entry {
          display: grid;
          grid-template-columns: auto 1fr auto auto;
          gap: 12px;
          font-size: 0.7rem;
          padding: 4px 6px;
          background: rgba(0, 255, 128, 0.05);
          border-radius: 2px;
          align-items: center;
        }

        .history-time {
          color: #66ff99;
        }

        .history-op {
          color: #00ff80;
        }

        .history-duration {
          color: #99ffcc;
        }

        .history-status.success {
          color: #00ff00;
        }

        .history-status.error {
          color: #ff0000;
        }

        /* Dark Editor Styles */
        .dark-editor {
          width: 100%;
          height: 100vh;
          background: linear-gradient(135deg, rgba(0, 5, 2, 0.98) 0%, rgba(0, 8, 4, 0.98) 100%);
          display: flex;
          flex-direction: column;
        }

        .editor-frame {
          display: flex;
          flex-direction: column;
          height: 100%;
          margin: 6px;
          border: 2px solid #00ff80;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 0 30px rgba(0, 255, 128, 0.2);
        }

        .control-strip {
          height: 48px;
          background: rgba(0, 255, 128, 0.12);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid #00ff80;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 16px;
          font-size: 0.8rem;
        }

        .strip-left, .strip-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .strip-center {
          flex: 1;
          display: flex;
          justify-content: center;
        }

        .strip-button {
          background: rgba(0, 255, 128, 0.15);
          border: 1px solid rgba(0, 255, 128, 0.4);
          border-radius: 3px;
          color: #00ff80;
          padding: 4px 8px;
          font-size: 0.75rem;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 4px;
          letter-spacing: 0.3px;
        }

        .strip-button:hover:not(:disabled) {
          background: rgba(0, 255, 128, 0.25);
          border-color: rgba(0, 255, 128, 0.6);
          transform: translateY(-1px);
        }

        .strip-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          transform: none;
        }

        .system-metrics {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #66ff99;
          font-size: 0.75rem;
          letter-spacing: 0.5px;
        }

        .word-counter {
          color: #99ffcc;
          font-size: 0.75rem;
          letter-spacing: 0.3px;
        }

        .mini-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(0, 255, 128, 0.3);
          border-top-color: #00ff80;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        /* Simplified Dark Mode Interface */
        .simplified-editor-interface {
          height: calc(100% - 48px);
          display: flex;
          flex-direction: column;
          background: rgba(0, 0, 0, 0.8);
        }

        .dark-simple-textarea {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: #00ff80;
          font-family: inherit;
          font-size: 0.9rem;
          line-height: 1.5;
          padding: 16px 20px;
          resize: none;
          overflow-y: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
          margin-bottom: 12px;
        }

        .dark-simple-textarea::placeholder {
          color: rgba(0, 255, 128, 0.4);
          font-style: italic;
        }

        .dark-simple-textarea::-webkit-scrollbar {
          width: 8px;
        }

        .dark-simple-textarea::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
        }

        .dark-simple-textarea::-webkit-scrollbar-thumb {
          background: rgba(0, 255, 128, 0.4);
          border-radius: 4px;
        }

        .dark-simple-textarea::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 255, 128, 0.6);
        }

        .compact-status-panel {
          background: rgba(0, 0, 0, 0.6);
          border-top: 1px solid rgba(0, 255, 128, 0.3);
          padding: 12px 20px;
          font-size: 0.75rem;
        }

        .status-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          color: #66ff99;
        }

        .status-row:last-child {
          margin-bottom: 0;
        }

        .status-row span {
          color: #00ff80;
        }

        .status-value.ready {
          color: #00ff00;
        }

        .status-value.processing {
          color: #ffa500;
          animation: pulse 1s infinite;
        }

        .status-value.error {
          color: #ff0000;
        }

        .error-console {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 0, 0, 0.15);
          border: 1px solid rgba(255, 0, 0, 0.5);
          border-radius: 4px;
          color: #ff6666;
          padding: 8px 12px;
          font-size: 0.8rem;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: slideUp 0.3s ease;
          backdrop-filter: blur(10px);
          font-family: inherit;
          letter-spacing: 0.3px;
        }

        .error-code {
          color: #ff4444;
          font-weight: bold;
        }

        .error-text {
          color: #ff8888;
        }

        .editor-interface {
          height: calc(100% - 48px);
          display: flex;
          background: rgba(0, 0, 0, 0.8);
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }

        @media (max-width: 768px) {
          .primary-editor {
            padding: 8px;
          }
          
          .metrics-panel {
            flex-direction: column;
            gap: 12px;
          }
          
          .analysis-panel {
            border-left: none;
            border-top: 1px solid rgba(0, 255, 128, 0.3);
            padding-left: 0;
            padding-top: 12px;
          }

          .analysis-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .editor-frame {
            margin: 2px;
          }
          
          .control-strip {
            height: 44px;
            padding: 0 12px;
            font-size: 0.75rem;
          }
          
          .strip-button {
            padding: 3px 6px;
            font-size: 0.7rem;
          }
          
          .status-panel {
            width: 160px;
            padding: 12px;
          }
          
          .line-number-panel {
            width: 60px;
            padding: 6px;
          }
          
          .advanced-textarea {
            font-size: 0.85rem;
            padding: 12px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default ScientificEditorSystem; 